import { forwardRef, useCallback, useEffect, useRef, useState } from "react"

import { huduIconButtonClass } from "@/tiptap/lib/hudu-icon-button"
import { cn } from "@/tiptap/lib/tiptap-utils"
import { useFindReplace } from "@/tiptap/ui/find-replace-button/use-find-replace"

import { Button } from "@/tiptap/ui-primitive/button"
import { ButtonGroup } from "@/tiptap/ui-primitive/button-group"
import { Input } from "@/tiptap/ui-primitive/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/tiptap/ui-primitive/popover"
import { Card, CardBody } from "@/tiptap/ui-primitive/card"
import { ArrowDownIcon } from "@/tiptap/icons/arrow-down-icon"
import { ArrowUpIcon } from "@/tiptap/icons/arrow-up-icon"

const EDITOR_HEADER_STICKY_TOP_FALLBACK = 52

function getEditorHeader(editor, triggerEl) {
  if (triggerEl instanceof Element) {
    const fromTrigger = triggerEl.closest(".notion-like-editor-header")
    if (fromTrigger) return fromTrigger
  }

  const editorDom = editor?.view?.dom
  if (!(editorDom instanceof Element)) return null

  return editorDom
    .closest(".notion-like-editor-wrapper")
    ?.querySelector(".notion-like-editor-header") ?? null
}

function isEditorHeaderAtPageTop(header) {
  if (typeof window === "undefined" || !header) return false

  const stickyTop = Number.parseFloat(getComputedStyle(header).top)
  const threshold = Number.isFinite(stickyTop) ? stickyTop : EDITOR_HEADER_STICKY_TOP_FALLBACK
  return header.getBoundingClientRect().top > threshold + 1
}

function useEditorHeaderAtPageTop(editor, triggerRef) {
  const [atPageTop, setAtPageTop] = useState(false)

  useEffect(() => {
    const update = () => {
      const header = getEditorHeader(editor, triggerRef?.current)
      setAtPageTop(isEditorHeaderAtPageTop(header))
    }

    update()
    window.addEventListener("scroll", update, { capture: true, passive: true })
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, { capture: true })
      window.removeEventListener("resize", update)
    }
  }, [editor, triggerRef])

  return atPageTop
}

function FindReplacePanel({
  query,
  matchCase,
  wholeWord,
  count,
  current,
  hasMatch,
  initialReplace,
  setQuery,
  next,
  prev,
  replaceCurrent,
  replaceAll,
  onClose,
  atPageTop,
}) {
  const [findValue, setFindValue] = useState(query)
  const [replaceValue, setReplaceValue] = useState(initialReplace)

  useEffect(() => {
    setFindValue(query)
  }, [query])

  const dispatchQuery = useCallback((value, overrides = {}) => {
    setQuery({
      query: value,
      matchCase,
      wholeWord,
      ...overrides,
    })
  }, [matchCase, setQuery, wholeWord])

  const handleFindChange = useCallback((event) => {
    const value = event.target.value
    setFindValue(value)
    dispatchQuery(value)
  }, [dispatchQuery])

  const handleReplaceChange = useCallback((event) => {
    setReplaceValue(event.target.value)
  }, [])

  const handleFindKeyDown = useCallback((event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (event.shiftKey) {
        prev()
      } else {
        next()
      }
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      onClose()
    }
  }, [next, onClose, prev])

  const handleReplaceKeyDown = useCallback((event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      replaceCurrent(replaceValue)
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      onClose()
    }
  }, [onClose, replaceCurrent, replaceValue])

  const toggleMatchCase = useCallback(() => {
    dispatchQuery(findValue, { matchCase: !matchCase })
  }, [dispatchQuery, findValue, matchCase])

  const stopEditorHotkeys = useCallback((event) => {
    event.stopPropagation()
  }, [])

  const counterText = !findValue
    ? ""
    : count === 0
      ? "No results"
      : `${current + 1} / ${count}`

  return (
    <Card
      className={cn(
        "tiptap-find-replace-card",
        atPageTop && "tiptap-find-replace-card--page-top",
      )}
      onKeyDownCapture={stopEditorHotkeys}>
      <CardBody>
        <div className="tiptap-find-replace-row">
          <Input
            type="text"
            placeholder="Find"
            aria-label="Find"
            value={findValue}
            onChange={handleFindChange}
            onKeyDown={handleFindKeyDown}
            autoFocus
            className="tiptap-find-replace-input" />
          <ButtonGroup>
            <Button
              type="button"
              variant="ghost"
              aria-label="Previous match"
              tooltip="Previous match"
              disabled={!hasMatch}
              data-disabled={!hasMatch}
              onClick={prev}>
              <ArrowUpIcon className="tiptap-button-icon" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              aria-label="Next match"
              tooltip="Next match"
              disabled={!hasMatch}
              data-disabled={!hasMatch}
              onClick={next}>
              <ArrowDownIcon className="tiptap-button-icon" />
            </Button>
          </ButtonGroup>
        </div>

        <Input
          type="text"
          placeholder="Replace with"
          aria-label="Replace with"
          value={replaceValue}
          onChange={handleReplaceChange}
          onKeyDown={handleReplaceKeyDown}
          className="tiptap-find-replace-input" />

        <div className="tiptap-find-replace-footer">
          <Button
            type="button"
            variant="ghost"
            aria-label="Match case"
            tooltip="Match case"
            aria-pressed={matchCase}
            data-active-state={matchCase ? "on" : "off"}
            onClick={toggleMatchCase}>
            <span className="tiptap-button-text" style={{ fontWeight: 600 }}>Aa</span>
          </Button>
          <span className="tiptap-find-replace-counter" aria-live="polite">{counterText}</span>
          <div className="tiptap-find-replace-actions">
            <Button
              type="button"
              variant="ghost"
              aria-label="Replace"
              tooltip="Replace"
              disabled={!hasMatch}
              data-disabled={!hasMatch}
              onClick={() => replaceCurrent(replaceValue)}>
              <span className="tiptap-button-text">Replace</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              aria-label="Replace all"
              tooltip="Replace all"
              disabled={!hasMatch}
              data-disabled={!hasMatch}
              onClick={() => replaceAll(replaceValue)}>
              <span className="tiptap-button-text">Replace all</span>
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

export const FindReplaceButton = forwardRef((
  {
    editor: providedEditor,
    onClick,
    className,
    ...buttonProps
  },
  ref,
) => {
  const {
    editor,
    query,
    matchCase,
    wholeWord,
    count,
    current,
    hasMatch,
    uiOpen,
    label,
    Icon,
    setQuery,
    next,
    prev,
    replaceCurrent,
    replaceAll,
    clear,
    open,
    close,
  } = useFindReplace(providedEditor)
  const triggerRef = useRef(null)
  const atPageTop = useEditorHeaderAtPageTop(editor, triggerRef)
  const [replaceValue, setReplaceValue] = useState("")

  const assignTriggerRef = useCallback((node) => {
    triggerRef.current = node
    if (typeof ref === "function") {
      ref(node)
    } else if (ref) {
      ref.current = node
    }
  }, [ref])

  const prefillFromSelection = useCallback(() => {
    if (!editor) return

    const { from, to, empty } = editor.state.selection
    if (empty) return

    const selectedText = editor.state.doc.textBetween(from, to, " ").trim()
    if (!selectedText) return

    setQuery({ query: selectedText, matchCase, wholeWord })
  }, [editor, matchCase, setQuery, wholeWord])

  const handleOpenChange = useCallback((nextOpen) => {
    if (nextOpen) {
      open()
      prefillFromSelection()
    } else {
      clear()
      close()
    }
  }, [clear, close, open, prefillFromSelection])

  const handleTriggerClick = useCallback((event) => {
    onClick?.(event)
  }, [onClick])

  const handlePanelClose = useCallback(() => {
    clear()
    close()
  }, [clear, close])

  if (!editor) return null

  return (
    <Popover open={uiOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          role="button"
          tabIndex={-1}
          aria-label={label}
          tooltip={label}
          className={huduIconButtonClass(className)}
          unstyled
          data-active-state={uiOpen ? "on" : "off"}
          onClick={handleTriggerClick}
          {...buttonProps}
          ref={assignTriggerRef}>
          <Icon className="tiptap-button-icon" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end">
        <FindReplacePanel
          query={query}
          matchCase={matchCase}
          wholeWord={wholeWord}
          count={count}
          current={current}
          hasMatch={hasMatch}
          initialReplace={replaceValue}
          setQuery={setQuery}
          next={next}
          prev={prev}
          replaceCurrent={(value) => {
            setReplaceValue(value)
            replaceCurrent(value)
          }}
          replaceAll={(value) => {
            setReplaceValue(value)
            replaceAll(value)
          }}
          onClose={handlePanelClose}
          atPageTop={atPageTop} />
      </PopoverContent>
    </Popover>
  )
})

FindReplaceButton.displayName = "FindReplaceButton"

export default FindReplaceButton
